import { service } from '@maia/cart';
import { mapper } from '@maia/icons';
import { BillingUnitDto, ProductsDto } from '@maia/shared';
import {
  BigPricingPill,
  cardStyles,
  CompositionCard,
  FeaturedIcon,
  RadioList,
  Switch,
  Typography,
} from '@maia/ui';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { getCompositionsOptions } from '../../../../features/edit-insurance/get-compositions/queryOptions';
import { getProductsOptions } from '../../../../features/edit-insurance/get-products/queryOptionts';
import { Form } from '../../../../features/form/base-form/base-form';
import { calculateDefaultValues } from '../../../../features/form/utils/calculate-default-values';
import { mapToAnswer } from '../../../../features/form/utils/map-to-answers';
import { getCustomerOptions } from './../../../../features/customer/customer/queryOptions';
import { service as CmsService } from '@maia/cms';

export const Route = createFileRoute(
  '/_authenticated/edit-insurance/$customer/cart/$cart/_layout/compositions',
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { customer: customerGuid, cart },
  }) => {
    const products = await queryClient.ensureQueryData({
      ...getProductsOptions({
        cartGuid: cart,
        customerGuid: customerGuid,
      }),
    });

    const isProductWithComposition = products.some(
      (product) => product.isComposition,
    );

    const compositions = isProductWithComposition
      ? await queryClient.ensureQueryData(
          getCompositionsOptions({
            cartGuid: cart,
            customerGuid: customerGuid,
          }),
        )
      : null;

    const customer = await queryClient.ensureQueryData(
      getCustomerOptions(customerGuid),
    );
    return {
      customer,
      products,
      compositions,
    };
  },
});

function RouteComponent() {
  const {
    compositions: initialCompositionsData,
    products,
    customer,
  } = Route.useLoaderData();
  const { cart } = Route.useParams();

  const { data: compositions, refetch: refetchCompositions } = useQuery({
    ...getCompositionsOptions({
      cartGuid: cart,
      customerGuid: customer.guid,
    }),
    initialData: initialCompositionsData ?? [],
    enabled: products.some((product) => product.isComposition),
  });

  const results = useQueries({
    queries: compositions.map((c) => ({
      queryKey: ['compositions', c.guid],
      queryFn: async () => {
        const cmsData = await CmsService.getProductDetails({
          productGuid: c.guid,
        });
        return { ...cmsData, guid: c.guid };
      },
    })),
  });

  const { data: product, refetch: refetchProduct } = useQuery({
    ...getProductsOptions({
      cartGuid: cart,
      customerGuid: customer.guid,
    }),
    select: (data) => data[0],
    initialData: products,
  });

  const compositionSelectionForm = useForm({
    defaultValues: {
      compositionGuid: '',
    },
    onSubmit: ({ value }) => selectComposition(value.compositionGuid),
  });

  const { mutate: selectComposition, isPending: selectCompositionIsPending } =
    useMutation({
      mutationFn: (
        compositionGuid: Parameters<
          typeof service.putCompositionsMutation
        >[number]['compositionGuid'],
      ) =>
        service.putCompositionsMutation({
          cartGuid: cart,
          customerGuid: customer.guid,
          compositionGuid,
        }),
      onSuccess: () => refetchProduct(),
    });

  const { mutate: putAddonQuestion, isPending: putAddonIsPending } =
    useMutation({
      mutationFn: (
        questions: Parameters<
          typeof service.putPreconditionMutationQuestions
        >[number]['questions'],
      ) =>
        service.putPreconditionMutationQuestions({
          cartGuid: cart,
          customerGuid: customer.guid,
          questions,
        }),
      onSuccess: () => {
        refetchCompositions();
        return refetchProduct();
      },
    });

  const activeComposition = product.composition;
  const isRevalidating = selectCompositionIsPending || putAddonIsPending;

  return (
    <div
      key={activeComposition}
      className={twMerge(
        isRevalidating &&
          'pointer-events-none opacity-25 transition-opacity duration-200',
        'flex flex-col gap-y-48',
      )}
    >
      {product.isComposition && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            compositionSelectionForm.handleSubmit();
          }}
          className="flex gap-x-32"
        >
          {compositions?.map((composition) => (
            <compositionSelectionForm.Field
              key={composition.guid}
              name="compositionGuid"
              listeners={{
                onChange: compositionSelectionForm.handleSubmit,
              }}
            >
              {(field) => {
                const isActive = activeComposition === composition.guid;
                return (
                  <CompositionCard
                    title={composition.product_name}
                    price={Number(composition.minimum_price)}
                    activities={composition.insured_activities.map(
                      (activity) => ({
                        title: activity.description,
                        icon: activity.icon,
                      }),
                    )}
                    onToggle={() => field.setValue(composition.guid)}
                    isActive={isActive}
                    billingUnit={customer.billingUnit}
                    moreInfo={
                      results.find((res) => res.data?.guid === composition.guid)
                        ?.data
                    }
                  />
                );
              }}
            </compositionSelectionForm.Field>
          ))}
        </form>
      )}
      {(activeComposition || !product.isComposition) && (
        <CompositionQuestions
          products={product}
          onSubmit={putAddonQuestion}
          billingUnit={customer.billingUnit}
        />
      )}
    </div>
  );
}

type CompositionQuestionsProps = {
  products: ProductsDto;
  onSubmit: (value: any) => void;
  billingUnit: BillingUnitDto;
};

function CompositionQuestions(props: CompositionQuestionsProps) {
  const { products, onSubmit, billingUnit } = props;

  const questions = useMemo(
    () => [
      ...(products.addOns?.map((addon) => ({
        ...addon,
        revalidateOnChange: true,
      })) || []),
      ...(products.addOns?.flatMap(
        (addon) =>
          addon.children?.map((child) => ({
            ...child,
            revalidateOnChange: true,
          })) || [],
      ) || []),
    ],
    [products],
  );

  const defaultValues = useMemo(
    () => calculateDefaultValues(questions),
    [questions],
  );

  const addonsForm = useForm({
    defaultValues,
    onSubmit: ({ value }) => onSubmit(mapToAnswer(value)),
  });

  const productQuestions = useMemo(
    () =>
      products.questions.map((question) => ({
        ...question,
        revalidateOnChange: true,
      })),
    [products],
  );

  if (!questions.length && !productQuestions.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-48">
      {!!productQuestions.length && (
        <div>
          <Typography
            variant="body-l"
            weight="semibold"
            className="inline-flex mb-32"
          >
            Configureer het product
          </Typography>

          <Form
            className="flex flex-col gap-y-32"
            questions={productQuestions}
            onRevalidate={(values) => onSubmit(mapToAnswer(values))}
          />
        </div>
      )}
      {!!questions.length && (
        <div>
          <Typography
            variant="body-l"
            weight="semibold"
            className="inline-flex mb-32"
          >
            Selecteer eventuele uitbreidingen
          </Typography>
          <form className="flex flex-col gap-y-32">
            {products.addOns?.map((addon) => (
              <addonsForm.Field
                key={addon.guid}
                name={addon.guid}
                listeners={{
                  onChange: () => addonsForm.handleSubmit(),
                }}
              >
                {(field) => (
                  <div
                    className={cardStyles({
                      variant: 'elevated',
                      className: 'flex flex-col gap-y-20 p-20',
                    })}
                  >
                    <div className="flex items-center gap-x-12">
                      <FeaturedIcon
                        icon={mapper(addon.icon)}
                        variant="light-square"
                        size="xl"
                      />
                      <div className="flex flex-col gap-y-4 grow">
                        <Typography variant="display6" weight="semibold">
                          {addon.phrasing}
                        </Typography>
                        {/* <Typography variant="body-s" weight="regular">
                        {addon.description}
                      </Typography> */}
                      </div>
                      <BigPricingPill
                        price={Number(addon.price?.premiumAfterTax ?? '0')}
                        variant="ghost"
                        size="lg"
                        billingUnit={billingUnit}
                      />

                      <Switch
                        intent="on/off"
                        isSelected={addon.answer.value === 'J'}
                        onChange={(value) =>
                          field.handleChange(
                            value
                              ? addon.options[0].value
                              : addon.options[1].value,
                          )
                        }
                      />
                    </div>
                    {field.state.value === addon.options[0].value &&
                      addon.children?.map((child) => (
                        <addonsForm.Field
                          key={child.guid}
                          name={child.guid}
                          listeners={{
                            onChange: addonsForm.handleSubmit,
                          }}
                        >
                          {(field) => (
                            <RadioList
                              variant="outlined"
                              items={child.options}
                              onChange={field.handleChange}
                              value={child.answer.value}
                            />
                          )}
                        </addonsForm.Field>
                      ))}
                  </div>
                )}
              </addonsForm.Field>
            ))}
          </form>
        </div>
      )}
    </div>
  );
}
